import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import { IconCancel, IconClose, IconErrorCircle, IconFileUpload, IconResetSmall } from "@mzc-cloudplex/icons";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import theme from "@styles/theme";
import StickyHeaderDialog from "@components_v2/dialog/sticky-header-dialog";

import TextField from "@components_v2/text-field/text-field";
import Tooltip from "@components_v2/Tooltip/tooltip";
import AutocompleteSmallTableList from "@features/autocomplete-table/autocomplete-small-table-list";
import { createMyRequest, createRequest } from "@modules/requests";
import { setNotification } from "@modules/notification";
import { getRequestCreateRequestMessage } from "@cores/notification/message";
import { useDispatch, useSelector } from "react-redux";
import validateInput from "@cores/validateInput";
import { LIMIT_CHAR, REQUEST_CATEGORY } from "@constants";
import useCheckPermission from "@hooks/useCheckPermission";

const DialogRequest = ({ open, onClose, afterMutation, projectId, assets, requirePermissionCheck = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const MAX_ASSETS = 20;
    const MAX_ATTACHMENTS = 20;

    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });

    const [editValue, setEditValue] = useState({
        projectId,
        category: REQUEST_CATEGORY.COMMON,
    });
    const [validationErrors, setValidationErrors] = useState({});
    const fileInputRef = useRef(null);

    const requests = useSelector(({ requests }) => requests);

    const attachmentColumn = useMemo(() => {
        return [
            {
                id: "title",
                label: "Name",
                style: { custom: { flex: 1, width: "calc(100% - 283px)" } },
                disabled: true,
                checked: true,
                sortable: false,
                useInAutocomplete: true,
            },
            {
                id: "extensions",
                label: "Extensions",
                style: { custom: { width: 135 } },
                disabled: false,
                checked: true,
                sortable: false,
            },
            {
                id: "size",
                label: "Size",
                style: { custom: { width: 148 } },
                disabled: false,
                checked: true,
                sortable: false,
                divider: true,
            },
        ];
    }, []);

    const assetsColumns = useMemo(() => {
        return [
            {
                id: "name",
                label: "Name",
                style: { custom: { flex: 1, width: "calc(100% - 148px)" } },
                disabled: true,
                checked: true,
                sortable: false,
                useInAutocomplete: true,
            },
            {
                id: "extensions",
                label: "Extensions",
                style: { custom: { width: 148 } },
                disabled: false,
                checked: true,
                sortable: false,
            },
        ];
    }, []);

    const onHandleChangeValue = (key, value) => {
        setEditValue({
            ...editValue,
            [key]: value,
        });
    };

    const onHandleValidationErrors = (key, value, validation) => {
        setValidationErrors((prev) => ({ ...prev, [key]: validateInput(value, validation) }));
    };

    const onHandleChangeTitle = (e) => {
        const value = e.target.value;
        onHandleValidationErrors("title", value, { required: true, maxLength: LIMIT_CHAR.title });
        onHandleChangeValue("title", value);
    };

    const onHandleChangeDescription = (e) => {
        const value = e.target.value;
        onHandleValidationErrors("description", value, { maxLength: LIMIT_CHAR.description });
        onHandleChangeValue("description", value);
    };

    const onHandleDeleteAsset = useCallback(
        (assetId) => {
            setEditValue({
                ...editValue,
                assets: editValue.assets.filter(({ id }) => id !== assetId),
            });
        },
        [editValue],
    );

    const onHandleDeleteAttachment = (fileIndex) => {
        const _files = editValue.files.filter((_, index) => index !== Number(fileIndex));
        setEditValue({
            ...editValue,
            files: _files,
        });
    };

    const onAddfiles = useCallback(
        (e) => {
            try {
                const files = e.target.files;
                if (!files) return;

                const _files = Object.values(files).reduce((acc, cur) => {
                    acc.push({
                        file: cur,
                        title: cur.name.split(".")?.[0],
                        type: cur.type,
                        size: cur.size,
                        extensions: cur.name.split(".").pop().toLowerCase(),
                    });

                    return acc;
                }, []);

                setEditValue({
                    ...editValue,
                    files: (editValue.files ?? []).concat(_files),
                });
            } catch (error) {
                console.error(error);
            }
        },
        [editValue],
    );

    const onClickSubmit = async () => {
        try {
            let params = {
                ...editValue,
            };
            if (editValue.assets) params.assets = params.assets.map(({ id }) => ({ id }));

            if (requirePermissionCheck) await dispatch(createRequest(params));
            else await dispatch(createMyRequest(params));

            await dispatch(
                setNotification({
                    type: "info",
                    contents: getRequestCreateRequestMessage({ name: params.title }),
                }),
            );
            onClose();
            afterMutation();
        } catch (error) {
            console.error(error);
        }
    };

    const initSelectedAssets = useCallback(() => {
        setEditValue((prev) => ({
            ...prev,
            assets,
        }));
    }, [assets]);

    useEffect(() => {
        if (assets && assets.length > 0) {
            initSelectedAssets();
        }
    }, [assets, initSelectedAssets]);

    return (
        <StickyHeaderDialog onClose={onClose} open={open} variant="expandable" maxWidth={"lg"}>
            <DialogTitle>
                <Grid container>
                    <Typography variant={"h3"}>{t(`common::label::Request`, `Request`)}</Typography>
                </Grid>
                <TooltipButton
                    circled
                    title={t(`common::label::Close`, "Close")}
                    onClickButton={onClose}
                    buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                />
            </DialogTitle>
            <DialogContent
                sx={{ height: "100%", "&.MuiDialogContent-root": { width: "840px", pl: 3, pr: 1.5, pt: 2.5, pb: 3 } }}
            >
                <Grid container flexDirection={"column"}>
                    <Box mb={3}>
                        <Typography required variant="body1_500">
                            {t(`common::label::Title`, `Title`)}
                        </Typography>
                        <TextField
                            value={editValue.title}
                            error={validationErrors?.title?.level}
                            helperText={validationErrors?.title?.message}
                            onChange={onHandleChangeTitle}
                            placeholder={t(`common::label::Title`, `Title`)}
                        />
                    </Box>
                    <Box mb={1.5}>
                        <Typography variant="body1_500">{t(`common::label::Description`, `Description`)}</Typography>
                        <TextField
                            value={editValue.description}
                            error={validationErrors?.description?.level}
                            helperText={validationErrors?.description?.message}
                            multiline
                            length={(editValue.description || "").length}
                            maxLength={LIMIT_CHAR.description}
                            placeholder={t(
                                "common::msg::Please enter the description.",
                                "Please enter the description.",
                            )}
                            onChange={onHandleChangeDescription}
                        />
                    </Box>
                    <Box mb={3}>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<IconFileUpload size={16} />}
                            disabled={requirePermissionCheck && !ASSETS_PERMISSIONS.EDIT}
                        >
                            {t(`common::label::Attachment`, `Attachment`)}
                            <input
                                ref={fileInputRef}
                                id={"upload"}
                                type={"file"}
                                multiple
                                onChange={onAddfiles}
                                style={{
                                    clip: "rect(0 0 0 0)",
                                    clipPath: "inset(50%)",
                                    height: 1,
                                    overflow: "hidden",
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    whiteSpace: "nowrap",
                                    width: 1,
                                }}
                            />
                        </Button>
                    </Box>
                    {!!editValue.files && (
                        <Box mb={3}>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                    <Typography variant="body1_500">
                                        {t(`common::label::Selected Attachment`, `Selected Attachment`)} (
                                        <Typography
                                            variant={"body1_500"}
                                            color={editValue.files?.length > MAX_ATTACHMENTS ? "error" : "primary"}
                                        >
                                            {editValue.files?.length ?? 0}
                                        </Typography>
                                        /{MAX_ATTACHMENTS})
                                    </Typography>
                                    {editValue.files?.length > MAX_ATTACHMENTS && (
                                        <IconErrorCircle size={16} htmlColor={theme.palette.error.main} />
                                    )}
                                </Stack>
                                <TooltipButton
                                    circled
                                    size={"small"}
                                    onClickButton={() => {
                                        onHandleChangeValue("files", []);
                                        // 파일 input의 value를 초기화
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = "";
                                        }
                                    }}
                                    buttonIcon={<IconResetSmall size={16} />}
                                    title={t("common::label::Delete all", "Delete all")}
                                />
                            </Stack>
                            <AutocompleteSmallTableList
                                type={"asset"}
                                data={editValue.files ?? []}
                                columns={attachmentColumn}
                                disableThumbnail={true}
                                deleteButton={
                                    <Tooltip title={t(`common::label::Delete`)}>
                                        <IconButton
                                            size="medium"
                                            color={"inherit"}
                                            circled
                                            onClick={(e) => {
                                                onHandleDeleteAttachment(e.target.closest("tr").dataset.id);
                                            }}
                                        >
                                            <IconCancel size={12} htmlColor={theme.palette.grey[700]} />
                                        </IconButton>
                                    </Tooltip>
                                }
                                gridStyle={{ mt: 1 }}
                            />
                        </Box>
                    )}
                    {assets && assets.length > 0 && (
                        <Box mb={2}>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                    <Typography variant="body1_500">
                                        {t(`common::label::Selected Assets`, `Selected Assets`)} (
                                        <Typography
                                            variant={"body1_500"}
                                            color={editValue.assets?.length > MAX_ASSETS ? "error" : "primary"}
                                        >
                                            {editValue.assets?.length ?? 0}
                                        </Typography>
                                        /{MAX_ASSETS})
                                    </Typography>
                                    {editValue.assets?.length > MAX_ASSETS && (
                                        <IconErrorCircle size={16} htmlColor={theme.palette.error.main} />
                                    )}
                                </Stack>
                                <TooltipButton
                                    circled
                                    size={"small"}
                                    onClickButton={() => initSelectedAssets()}
                                    buttonIcon={<IconResetSmall size={16} />}
                                    title={t("common::label::Reset", "Reset")}
                                />
                            </Stack>
                            <AutocompleteSmallTableList
                                type={"asset"}
                                data={editValue.assets ?? []}
                                columns={assetsColumns}
                                disableThumbnail={true}
                                deleteButton={
                                    <Tooltip title={t(`common::label::Delete`)}>
                                        <IconButton
                                            size="medium"
                                            color={"inherit"}
                                            circled
                                            onClick={(e) => {
                                                onHandleDeleteAsset(e.target.closest("tr").dataset.id);
                                            }}
                                        >
                                            <IconCancel size={12} htmlColor={theme.palette.grey[700]} />
                                        </IconButton>
                                    </Tooltip>
                                }
                                gridStyle={{ mt: 1 }}
                            />
                        </Box>
                    )}
                </Grid>
            </DialogContent>
            <Divider flexItem />
            <DialogActions>
                <Button color="secondary" onClick={onClose} variant="text">
                    {t(`common::label::Cancel`, `Cancel`)}
                </Button>
                <Button
                    color="primary"
                    onClick={onClickSubmit}
                    variant="contained"
                    loading={requests.createPending}
                    disabled={
                        (requirePermissionCheck && !ASSETS_PERMISSIONS.EDIT) ||
                        Object.values(validationErrors).some((el) => !!el) ||
                        requests.createPending ||
                        !editValue.title ||
                        editValue.assets?.length > MAX_ASSETS ||
                        editValue.files?.length > MAX_ATTACHMENTS
                    }
                >
                    {t(`common::label::Send`, `Send`)}
                </Button>
            </DialogActions>
        </StickyHeaderDialog>
    );
};

const DialogRequestDownload = ({ onClose, afterMutation, projectId, assets }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const MAX_ASSETS = 20;
    const requests = useSelector(({ requests }) => requests);

    const [editValue, setEditValue] = useState({
        projectId,
        title: "Download",
        category: REQUEST_CATEGORY.DOWNLOAD_PERMISSION,
        assets,
    });
    const [validationErrors, setValidationErrors] = useState({});

    const onHandleChangeValue = (key, value) => {
        setEditValue({
            ...editValue,
            [key]: value,
        });
    };

    const onHandleValidationErrors = (key, value, validation) => {
        setValidationErrors((prev) => ({ ...prev, [key]: validateInput(value, validation) }));
    };

    const onHandleChangeDescription = (e) => {
        const value = e.target.value;
        onHandleValidationErrors("description", value, { maxLength: LIMIT_CHAR.description });
        onHandleChangeValue("description", value);
    };

    const assetsColumns = useMemo(() => {
        return [
            {
                id: "name",
                label: "Name",
                style: { custom: { flex: 1, width: "calc(100% - 148px)" } },
                disabled: true,
                checked: true,
                sortable: false,
                useInAutocomplete: true,
            },
            {
                id: "extensions",
                label: "Extensions",
                style: { custom: { width: 148 } },
                disabled: false,
                checked: true,
                sortable: false,
            },
        ];
    }, []);

    const onClickSubmit = async () => {
        try {
            const params = {
                ...editValue,
                assets: editValue.assets.map(({ id }) => ({ id })),
            };
            await dispatch(createMyRequest(params));
            await dispatch(
                setNotification({
                    type: "info",
                    contents: getRequestCreateRequestMessage({ name: params.title }),
                }),
            );
            onClose();
            afterMutation();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <StickyHeaderDialog onClose={() => onClose} open={true} variant="expandable" maxWidth={"lg"}>
            <DialogTitle>
                <Grid container>
                    <Typography variant={"h3"}>{t(`common::label::Request download`, `Request download`)}</Typography>
                </Grid>
                <TooltipButton
                    circled
                    title={t(`common::label::Close`, "Close")}
                    onClickButton={onClose}
                    buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                />
            </DialogTitle>
            <DialogContent
                sx={{ height: "100%", "&.MuiDialogContent-root": { width: "840px", pl: 3, pr: 1.5, pt: 2.5, pb: 3 } }}
            >
                <Grid container flexDirection={"column"}>
                    {assets?.length > 0 && (
                        <Box mb={2}>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                    <Typography variant="body1_500">
                                        {t(`common::label::Selected Assets`, `Selected Assets`)} (
                                        <Typography
                                            variant={"body1_500"}
                                            color={editValue.assets?.length > MAX_ASSETS ? "error" : "primary"}
                                        >
                                            {editValue.assets?.length ?? 0}
                                        </Typography>
                                        /{MAX_ASSETS})
                                    </Typography>
                                    {editValue.assets?.length > MAX_ASSETS && (
                                        <IconErrorCircle size={16} htmlColor={theme.palette.error.main} />
                                    )}
                                </Stack>
                            </Stack>
                            <AutocompleteSmallTableList
                                type={"asset"}
                                data={editValue.assets ?? []}
                                columns={assetsColumns}
                                disableThumbnail={true}
                                gridStyle={{ mt: 1 }}
                            />
                        </Box>
                    )}
                    <Box mb={2}>
                        <Typography variant="body1_500">{t(`common::label::Description`, `Description`)}</Typography>
                        <TextField
                            value={editValue.description}
                            error={validationErrors?.description?.level}
                            helperText={validationErrors?.description?.message}
                            multiline
                            length={(editValue.description || "").length}
                            maxLength={LIMIT_CHAR.description}
                            placeholder={t(
                                "common::msg::Please enter the description.",
                                "Please enter the description.",
                            )}
                            onChange={onHandleChangeDescription}
                        />
                    </Box>
                </Grid>
            </DialogContent>

            <Divider flexItem />
            <DialogActions>
                <Button color="secondary" onClick={onClose} variant="text">
                    {t(`common::label::Cancel`, `Cancel`)}
                </Button>
                <Button
                    color="primary"
                    onClick={onClickSubmit}
                    variant="contained"
                    loading={requests.createPending}
                    disabled={
                        Object.values(validationErrors).some((el) => !!el) ||
                        requests.createPending ||
                        editValue.assets?.length === 0 ||
                        editValue.assets?.length > MAX_ASSETS
                    }
                >
                    {t(`common::label::Send`, `Send`)}
                </Button>
            </DialogActions>
        </StickyHeaderDialog>
    );
};

export { DialogRequest, DialogRequestDownload };
